import React, { useState } from "react";
import {
  createAnnouncement,
  updateAnnouncement
} from "./adminAnnouncementService";

/**
 * Dev logger
 */
const devLog = (...args) => {
  if (process.env.NODE_ENV !== "production") {
    console.log("[AnnouncementForm]", ...args);
  }
};

/**
 * Convert datetime-local → UTC ISO string
 * Example:
 * 2026-02-18T11:53 → 2026-02-18T06:23:00Z
 */
const toUTCISOString = (localDateTime) => {

  if (!localDateTime) return null;

  const date = new Date(localDateTime);

  return date.toISOString();

};

/**
 * Convert backend OffsetDateTime → datetime-local format
 * Example:
 * 2026-02-18T06:23:00Z → 2026-02-18T11:53
 */
const toLocalInputFormat = (isoString) => {

  if (!isoString) return "";

  const date = new Date(isoString);

  const pad = (n) => String(n).padStart(2, "0");

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;

};

const AnnouncementForm = ({
  announcement,
  onSaved,
  onCancel
}) => {

  const [form, setForm] = useState({

    message: announcement?.message || "",

    priority: announcement?.priority ?? 0,

    enabled: announcement?.enabled ?? true,

    backgroundColor:
      announcement?.backgroundColor || "#0d6efd",

    textColor:
      announcement?.textColor || "#ffffff",

    link: announcement?.link || "",

    startTime: toLocalInputFormat(announcement?.startTime),

    endTime: toLocalInputFormat(announcement?.endTime)

  });

  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {

    const { name, value, type, checked } = e.target;

    const finalValue =
      type === "checkbox"
        ? checked
        : name === "priority"
          ? parseInt(value || 0)
          : value;

    devLog("Change:", name, finalValue);

    setForm(prev => ({
      ...prev,
      [name]: finalValue
    }));

  };


  const save = async () => {

    if (!form.message.trim()) {

      alert("Message is required");
      return;

    }

    setLoading(true);

    try {

      // Convert to OffsetDateTime ISO format
      const payload = {

        ...form,

        startTime: toUTCISOString(form.startTime),

        endTime: toUTCISOString(form.endTime)

      };

      devLog("Sending payload:", payload);

      let response;

      if (announcement) {

        response = await updateAnnouncement(
          announcement.id,
          payload
        );

      } else {

        response = await createAnnouncement(payload);

      }

      if (
        response.status === 200 ||
        response.status === 201
      ) {

        devLog("Save success");

        onSaved();

      } else {

        alert(response.message || "Save failed");

      }

    } catch (e) {

      devLog("Save error:", e);

      alert("Error saving announcement");

    } finally {

      setLoading(false);

    }

  };


  return (

    <div className="form-card">

      <div className="form-header">

        <h2>
          {announcement
            ? "Edit Announcement"
            : "Create Announcement"}
        </h2>

      </div>


      <div className="form-group full-width">

        <label>Message *</label>

        <textarea
          name="message"
          value={form.message}
          onChange={handleChange}
          rows={3}
        />

      </div>


      <div className="form-grid">

        <div className="form-group">

          <label>Priority</label>

          <input
            name="priority"
            type="number"
            value={form.priority}
            onChange={handleChange}
          />

        </div>


        <div className="form-group checkbox-group">

          <label>Enabled</label>

          <input
            name="enabled"
            type="checkbox"
            checked={form.enabled}
            onChange={handleChange}
          />

        </div>


        <div className="form-group">

          <label>Background Color</label>

          <input
            name="backgroundColor"
            type="color"
            value={form.backgroundColor}
            onChange={handleChange}
          />

        </div>


        <div className="form-group">

          <label>Text Color</label>

          <input
            name="textColor"
            type="color"
            value={form.textColor}
            onChange={handleChange}
          />

        </div>


        <div className="form-group full-width">

          <label>Link</label>

          <input
            name="link"
            value={form.link}
            onChange={handleChange}
          />

        </div>


        <div className="form-group">

          <label>Start Time</label>

          <input
            name="startTime"
            type="datetime-local"
            value={form.startTime}
            onChange={handleChange}
          />

        </div>


        <div className="form-group">

          <label>End Time</label>

          <input
            name="endTime"
            type="datetime-local"
            value={form.endTime}
            onChange={handleChange}
          />

        </div>

      </div>


      <div className="form-actions">

        <button
          className="btn primary"
          onClick={save}
          disabled={loading}
        >
          {loading ? "Saving..." : "Save"}
        </button>

        <button
          className="btn secondary"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </button>

      </div>

    </div>

  );

};

export default AnnouncementForm;
